import { useEffect, useRef, useState } from "react";
import zoomSdk from "@zoom/appssdk";
import { AIMS_STATUS, SUMMARIZE_STATES, ROLES } from "../constants";
import { AIMS_START_DISCLAIMER_CHAT_MESSAGE } from "../features/transcript/transcript.constants";
import { fetchRtmsSessionStatus, SESSION_STATUS } from "../utils/rtmsSessionStatusUtils";
import { usePreference } from "../context/PreferenceContext";
import LogManager from "../utils/logManager";

const SUMMARIZATION_ELIGIBLE_STATUSES = [
  AIMS_STATUS.AVAILABLE,
  AIMS_STATUS.SUMMARY_TO_ORGANIZER,
];

/**
 * Hook that triggers auto-start summarization when all conditions are met.
 * Shared between the legacy UI (SummarizeBox) and the default UI (DefaultView/Home).
 *
 * Returns:
 *   isAutoStarting - true while async checks are in progress (show loading UI)
 *   autoStartFailed - true if auto-start attempted but failed (show warning modal)
 *   dismissAutoStartFailed - function to reset autoStartFailed to false
 *
 * This is for summarization auto-start only — completely separate from
 * ZMDS transcript auto-start (isAutostarting/enableAutoStart/audioStreamAutoStart),
 * which is decided in inMeetingInitializer and acted on by TranscribeAvailable, and reads
 * no preference state. Nothing in this hook can start or prevent transcription.
 */
const useAutoStartSummarization = ({ role, aimsStatus, summarizeState, meetingUuid, appContext, onSummarize }) => {
  const { isLoaded, loadFailed, loadFailureStatus, initialAutoStart } = usePreference();
  const guardRef = useRef(false);
  const onSummarizeRef = useRef(onSummarize);
  const [isAutoStarting, setIsAutoStarting] = useState(false);
  const [autoStartFailed, setAutoStartFailed] = useState(false);
  const [autoStartCompleted, setAutoStartCompleted] = useState(false);

  useEffect(() => {
    onSummarizeRef.current = onSummarize;
  }, [onSummarize]);

  const dismissAutoStartFailed = () => setAutoStartFailed(false);

  useEffect(() => {
    if (!isLoaded) return;
    if (role !== ROLES.ORGANIZER) {
      LogManager.info("Auto-start summarization: skipped, role =", role);
      return;
    }
    if (!SUMMARIZATION_ELIGIBLE_STATUSES.includes(aimsStatus)) {
      LogManager.info("Auto-start summarization: skipped, aimsStatus =", aimsStatus);
      return;
    }
    if (summarizeState !== SUMMARIZE_STATES.DEFAULT) {
      LogManager.info("Auto-start summarization: skipped, summarizeState =", summarizeState);
      return;
    }
    if (!appContext) {
      LogManager.info("Auto-start summarization: waiting for appContext");
      return;
    }
    if (guardRef.current) {
      LogManager.info("Auto-start summarization: skipped, already attempted");
      return;
    }

    // Preferences unreadable. A 403 is expected and returns quietly; anything else
    // is a real fault and reports failure.
    //
    // 403 means this session is denied /preferences by design (it is off the HIRE
    // endpoint allowlist), so we cannot know whether auto-start was wanted — the
    // setting we failed to read is the opt-in itself. Reporting a failure to someone
    // who never enabled it describes something that did not happen, and the manual
    // control still works.
    //
    // Everything else — 5xx, the 20s timeout, network, parse — keeps the failure
    // path. Do not widen the quiet branch to cover them: that hides genuine
    // /preferences outages from Federate organizers, who have no HIRE involvement.
    if (loadFailed) {
      if (loadFailureStatus === 403) {
        LogManager.info(
          "Auto-start summarization: not applicable, /preferences denied (403) so the opt-in cannot be read"
        );
        // Deliberately not AutoStart_Skipped. The other reasons on that counter
        // (preference_disabled, session_exists) are decisions not to do something
        // that was otherwise going to happen; this was never applicable. `reason` is
        // a property rather than a dimension, so an alarm on AutoStart_Skipped could
        // not filter it out.
        LogManager.metric("AutoStartSummarization_PreferencesDenied", 1);
        return;
      }

      LogManager.error(
        "Auto-start summarization: FAILED - /preferences unreadable, status =",
        loadFailureStatus ?? "none"
      );
      LogManager.metric("AutoStart_Failed", 1, { reason: "preference_api_error" });
      setAutoStartFailed(true);
      return;
    }

    if (initialAutoStart !== true) {
      LogManager.info("Auto-start summarization: skipped, preference not enabled (initialAutoStart =", initialAutoStart, ")");
      LogManager.metric("AutoStart_Skipped", 1, { reason: "preference_disabled" });
      return;
    }

    LogManager.info("Auto-start summarization: all conditions met, checking session status for meeting:", meetingUuid);

    const checkAndAutoStart = async () => {
      setIsAutoStarting(true);
      try {
        const sessionStatus = await fetchRtmsSessionStatus(meetingUuid);
        LogManager.info("Auto-start summarization: /session returned status =", sessionStatus.status);

        if (sessionStatus.status === SESSION_STATUS.CONNECT_FAIL || sessionStatus.status === SESSION_STATUS.ERROR) {
          LogManager.error("Auto-start summarization: FAILED - /session returned", sessionStatus.status);
          LogManager.metric("AutoStart_Failed", 1, { reason: "session_" + sessionStatus.status });
          setIsAutoStarting(false);
          setAutoStartFailed(true);
          setAutoStartCompleted(true);
          return;
        }

        if (sessionStatus.status !== SESSION_STATUS.NOT_FOUND) {
          LogManager.info("Auto-start summarization: skipped, session already exists with status:", sessionStatus.status);
          LogManager.metric("AutoStart_Skipped", 1, { reason: "session_exists" });
          guardRef.current = true;
          setIsAutoStarting(false);
          setAutoStartCompleted(true);
          return;
        }

        guardRef.current = true;
        LogManager.info("Auto-start summarization: SUCCESS - triggering onSummarize for meeting:", meetingUuid);
        LogManager.metric("AutoStart_Triggered", 1);
        try {
          await zoomSdk.sendMessageToChat({
            message: AIMS_START_DISCLAIMER_CHAT_MESSAGE,
          });
          LogManager.info("Auto-start summarization: sent disclaimer message to meeting chat");
        } catch (error) {
          LogManager.error("Auto-start summarization: failed to send disclaimer message:", error);
        }
        // Invoke the controller directly so auto-start does not open the manual-start disclaimer modal.
        onSummarizeRef.current();
        setIsAutoStarting(false);
        setAutoStartCompleted(true);
      } catch (error) {
        LogManager.error("Auto-start summarization: FAILED - session check error, falling back to manual:", error);
        LogManager.metric("AutoStart_Failed", 1, { reason: "session_check_error" });
        setIsAutoStarting(false);
        setAutoStartFailed(true);
        setAutoStartCompleted(true);
      }
    };

    checkAndAutoStart();
    // loadFailureStatus belongs here alongside loadFailed: the branch above now
    // reads it to decide skip-vs-fail, and both are set in the same catch, so
    // omitting it would let the effect decide on a stale status.
  }, [meetingUuid, role, aimsStatus, summarizeState, isLoaded, appContext, loadFailed, loadFailureStatus, initialAutoStart]);

  return { isAutoStarting, autoStartFailed, dismissAutoStartFailed, autoStartCompleted };
};

export default useAutoStartSummarization;
